import { useAirQualityData, SensorType } from 'common';
import React from 'react';

import { useAppSync } from '../../contexts/AppSyncContext';

interface UseThermostatDataOptions {
  days?: number;
  selectedHubId?: string;
  indoor?: boolean;
  sensorId?: string;
}

interface ThermostatChartData {
  value: number;
  label: string;
  timestamp?: number;
}

interface UseThermostatDataReturn {
  thermostatData: ThermostatChartData[];
  loading: boolean;
  error: string | null;
  airQualitySensor: any;
  todayTemperature: number;
}

export const useThermostatData = (
  options: UseThermostatDataOptions = {},
): UseThermostatDataReturn => {
  const { sensors, selectedHubId: contextSelectedHubId } = useAppSync();
  const { days = 7, selectedHubId, indoor, sensorId } = options;

  // Use provided selectedHubId or fall back to context
  const hubId = selectedHubId || contextSelectedHubId;

  // Find the air quality sensor - use provided sensorId if available, otherwise search
  const airQualitySensor = React.useMemo(() => {
    if (sensorId) {
      return sensors.find((sensor) => sensor.id === sensorId);
    }

    return sensors.find(
      (sensor) =>
        sensor.hubId === hubId &&
        sensor.type === SensorType.AIR_QUALITY &&
        (indoor === undefined ||
          !sensor.sensorGroupConfiguration ||
          sensor.sensorGroupConfiguration.indoor === indoor),
    );
  }, [sensors, sensorId, hubId, indoor]);

  const { data, loading, error } = useAirQualityData({
    sensorId: airQualitySensor?.id,
    days,
    enabled: !!airQualitySensor?.id,
  });

  // Transform the data to format required by HubLineChart, grouping by day of week
  const thermostatData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group temperature data by day of week
    const dailyTemps = data.reduce(
      (acc, day) => {
        const date = new Date(day.label);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });

        if (!acc[dayOfWeek]) {
          acc[dayOfWeek] = { sum: 0, count: 0, timestamp: date.getTime() };
        }

        if (date.getTime() > acc[dayOfWeek].timestamp) {
          acc[dayOfWeek].timestamp = date.getTime();
        }

        // Add temperature in Fahrenheit to the accumulator
        acc[dayOfWeek].sum += (day.temperature * 9) / 5 + 32;
        acc[dayOfWeek].count += 1;

        return acc;
      },
      {} as Record<string, { sum: number; count: number; timestamp: number }>,
    );

    // Convert grouped data to chart format - show last 7 days ending with today
    const chartData: ThermostatChartData[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });

      const dayData = dailyTemps[dayLabel];

      if (dayData) {
        chartData.push({
          value: Math.round(dayData.sum / dayData.count),
          label: dayLabel,
          timestamp: dayData.timestamp,
        });
      } else {
        chartData.push({
          value: 0,
          label: dayLabel,
          timestamp: date.getTime(),
        });
      }
    }

    return chartData;
  }, [data, days]);

  const todayTemperature = React.useMemo(() => {
    if (!data || data.length === 0) return 0;

    const mostRecentData = [...data].sort(
      (a, b) => new Date(b.label).getTime() - new Date(a.label).getTime(),
    )[0];

    return Math.round((mostRecentData.temperature * 9) / 5 + 32);
  }, [data]);

  return {
    thermostatData,
    loading,
    error,
    airQualitySensor,
    todayTemperature,
  };
};
